<template>
  <div class="relative">
    <div class="flex space-x-1 md:space-x-2 items-center">
      <img
        v-for="language in displayedLanguages"
        :key="language"
        :src="`/img/flags/${language.toUpperCase()}.svg`"
        :alt="language"
        class="w-5 h-4 md:w-6 md:h-4 rounded object-cover"
        :class="
          selectedLanguages.includes(language)
            ? 'ring-2 yellow-400 ring-offset-2 ring-offset-Tourscanner_blue-source'
            : 'border border-zinc-600 dark:border-zinc-500'
        "
      />
      <span
        v-if="remainingLanguages.length > 0"
        @click.stop="toggleRemainingLanguages"
        class="cursor-pointer underline hover:text-gray-400 relative select-none text-sm md:text-md"
      >
        + {{ remainingLanguages.length }}
        <div
          v-if="showRemainingLanguages"
          class="absolute bottom-full left-0 mb-2 bg-gray-300 dark:bg-gray-600 shadow-lg rounded-lg p-2 w-10 select-none"
        >
          <div
            v-for="language in remainingLanguages"
            :key="language"
            class="flex items-center space-x-2 mb-1 select-none"
          >
            <img
              :src="`/img/flags/${language.toUpperCase()}.svg`"
              :alt="language"
              class="w-8 h-5 my-[5px] rounded object-cover pointer-events-none"
            />
          </div>
        </div>
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
const { locale } = useI18n();

const props = defineProps({
  languagesISO: {
    type: Array,
    required: true,
  },
  selectedLanguages: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const showRemainingLanguages = ref(false);

//Some languages are not supported by the flags, so we need to parse them
let parsedLanguages = [];
let displayedLanguages = [];
let remainingLanguages = [];
for (const language of props.languagesISO) {
  parsedLanguages.push(parseLanguage(language));
}

// Move selected languages to the beginning of the array
parsedLanguages.sort((a, b) => {
  if (props.selectedLanguages.includes(a) && !props.selectedLanguages.includes(b)) return -1;
  if (!props.selectedLanguages.includes(a) && props.selectedLanguages.includes(b)) return 1;
  return 0;
});

const preferredLanguageLocale = locale.value;

// Ensure preferredLanguageLocale is the first non-selected element if it exists in parsedLanguages
const preferredLanguageIndex = parsedLanguages.findIndex(
  (lang) => lang === preferredLanguageLocale && !props.selectedLanguages.includes(lang),
);
if (preferredLanguageIndex !== -1) {
  const [preferredLanguage] = parsedLanguages.splice(preferredLanguageIndex, 1);
  parsedLanguages.splice(props.selectedLanguages.length, 0, preferredLanguage);
}

// If preferredLanguageLocale is not present, ensure "gb" is the first non-selected element if it exists in parsedLanguages
if (preferredLanguageIndex === -1) {
  const gbIndex = parsedLanguages.findIndex((lang) => lang === 'gb' && !props.selectedLanguages.includes(lang));
  if (gbIndex !== -1) {
    const [gb] = parsedLanguages.splice(gbIndex, 1);
    parsedLanguages.splice(props.selectedLanguages.length, 0, gb);
  }
}

// If preferredLanguageLocale is present, ensure "gb" or "en" is the next element if it exists in parsedLanguages
else {
  const gbOrEnIndex = parsedLanguages.findIndex(
    (lang) => (lang === 'gb' || lang === 'en') && !props.selectedLanguages.includes(lang),
  );
  if (gbOrEnIndex !== -1 && gbOrEnIndex !== props.selectedLanguages.length + 1) {
    const [gbOrEn] = parsedLanguages.splice(gbOrEnIndex, 1);
    parsedLanguages.splice(props.selectedLanguages.length + 1, 0, gbOrEn);
  }
}

// Limit parsedLanguages to the first 4 elements
displayedLanguages = parsedLanguages.slice(0, 4);

// Assign the not shown languages to remainingLanguages
remainingLanguages = parsedLanguages.slice(4);

function parseLanguage(language) {
  return language;
}

// Function to check if a language is selected
function isSelected(language) {
  return props.selectedLanguages.includes(language);
}

// Function to toggle the visibility of remaining languages
function toggleRemainingLanguages() {
  showRemainingLanguages.value = !showRemainingLanguages.value;
}
</script>
